import axios from "axios";
import { getAuthHeader, getStoredToken } from "./auth";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:5001",
});

// Attaching token (if present) to each request
api.interceptors.request.use(
  async (config) => {
    try {
      const token = getStoredToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      } else if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
    } catch (e) {
      console.error("Error attaching token to request", e);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Deduplication & Memory/SWR Cache Engine ────────────────────────────────
const inFlightRequests = new Map();
const memoryCache = new Map();

const originalGet = api.get.bind(api);

/**
 * Deduplicated GET Request: Merges duplicate in-flight GET requests
 */
api.dedupedGet = (url, config = {}) => {
  const cacheKey = `${url}:${JSON.stringify(config.params || {})}`;
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const promise = originalGet(url, config)
    .finally(() => {
      inFlightRequests.delete(cacheKey);
    });

  inFlightRequests.set(cacheKey, promise);
  return promise;
};

/**
 * Transparent Stale-While-Revalidate (SWR) Cached GET
 * @param {string} url - API endpoint
 * @param {object} config - Axios config
 * @param {number} ttlMs - Cache time-to-live in ms (default: 2 minutes)
 */
api.cachedGet = async (url, config = {}, ttlMs = 120000) => {
  const cacheKey = `${url}:${JSON.stringify(config.params || {})}`;
  const now = Date.now();
  const cached = memoryCache.get(cacheKey);

  // Return fresh cache immediately
  if (cached && now - cached.timestamp < ttlMs) {
    return cached.data;
  }

  // If stale cache exists, revalidate in background and return stale data instantly
  if (cached) {
    api.dedupedGet(url, config).then((res) => {
      memoryCache.set(cacheKey, { timestamp: Date.now(), data: res });
    }).catch(() => {});
    return cached.data;
  }

  // No cache: fetch via deduplicated GET and cache result
  const res = await api.dedupedGet(url, config);
  memoryCache.set(cacheKey, { timestamp: Date.now(), data: res });
  return res;
};

/**
 * Intercept GET calls to automatically deduplicate simultaneous requests
 */
api.get = (url, config) => {
  // If caller explicitly disables cache/dedup, use originalGet
  if (config?.bypassDedup) {
    return originalGet(url, config);
  }
  return api.dedupedGet(url, config);
};

/**
 * Invalidate cache on mutations (POST, PUT, DELETE)
 */
const invalidateCache = () => {
  memoryCache.clear();
};

api.interceptors.response.use(
  (response) => {
    const method = response.config?.method?.toLowerCase();
    if (["post", "put", "delete", "patch"].includes(method)) {
      invalidateCache();
    }
    return response;
  },
  (error) => Promise.reject(error)
);

api.clearCache = invalidateCache;

export default api;
